import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconUser, IconMail, IconMapPin, IconLoader2 } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { PersonForm, AddressForm, DocumentForm } from '@/components/forms'
import { newClientSchema, normalizeDocumentNumber } from '@/lib/schemas'
import { useCreateClient } from '@/hooks/useCreateClient'

interface NouClientModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function NouClientModal({ open, onOpenChange, onSuccess }: NouClientModalProps) {
  const createClientMutation = useCreateClient()

  const form = useForm({
    resolver: zodResolver(newClientSchema),
    defaultValues: {
      nom: '',
      cognoms: '',
      num_document: '',
      tipus_document: '' as const,
      email: '',
      telefon: '',
      data_naixement: '',
      genere: 'no_especificat' as const,
      nacionalitat: '',
      adreca: '',
      ciutat: '',
      codi_postal: '',
      provincia: '',
      pais: 'Espanya',
      estat: 'actiu' as const,
      professio: '',
      telefon_emergencia: '',
      contacte_emergencia: '',
      preferencies_alimentaries: '',
      allergies: '',
      notes: '',
    },
  })

  const estats = [
    { value: 'actiu', label: 'Actiu' },
    { value: 'inactiu', label: 'Inactiu' },
  ]

  useEffect(() => {
    if (open) {
      form.reset()
    }
  }, [open, form])

  const onSubmit = async (data: any) => {
    // Preparar datos para el backend
    const dataToSend: any = {
      nom: data.nom,
      cognoms: data.cognoms,
      email: data.email,
    }

    // Campos opcionales
    if (data.telefon) dataToSend.telefon = data.telefon
    if (data.mobil) dataToSend.mobil = data.mobil
    // Document: els dos camps reals del formulari (abans llegia un `dni` que no existia i no
    // arribava mai al backend). El schema garanteix el tipus si hi ha número
    const numDocument = normalizeDocumentNumber(data.num_document)
    if (numDocument && data.tipus_document) {
      dataToSend.num_document = numDocument
      dataToSend.tipus_document = data.tipus_document
    }
    if (data.adreca) dataToSend.adreca = data.adreca
    if (data.ciutat) dataToSend.ciutat = data.ciutat
    if (data.codi_postal) dataToSend.codi_postal = data.codi_postal
    if (data.provincia) dataToSend.provincia = data.provincia
    if (data.pais) {
      const paisMap: { [key: string]: string } = {
        'Espanya': 'ES',
        'España': 'ES',
        'France': 'FR',
        'França': 'FR',
        'Portugal': 'PT',
      }
      dataToSend.pais = paisMap[data.pais] || data.pais.substring(0, 2).toUpperCase()
    }
    if (data.notes) dataToSend.notes = data.notes

    createClientMutation.mutate(dataToSend, {
      onSuccess: () => {
        onSuccess()
        onOpenChange(false)
        form.reset()
      },
      onError: (error: any) => {
        // Handle specific field errors if returned by backend
        if (error.response?.data?.errors) {
          Object.keys(error.response.data.errors).forEach(backendField => {
            const errorMessage = Array.isArray(error.response.data.errors![backendField])
              ? error.response.data.errors![backendField][0]
              : error.response.data.errors![backendField]

            form.setError(backendField as any, {
              type: 'server',
              message: errorMessage,
            })
          })
        }

        // 409 del backend (`ClientsService.create`): el missatge diu si és l'email o el document
        if (error.response?.status === 409) {
          const message: string = error.response?.data?.message ?? ''
          if (/email/i.test(message)) {
            form.setError('email', { type: 'server', message: 'Aquest email ja està registrat' })
          } else if (/document/i.test(message)) {
            form.setError('num_document', { type: 'server', message: 'Aquest document ja està registrat' })
          }
        }
      }
    })
  }

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-0 shadow-2xl backdrop-blur-xl">
        <div className="absolute top-4 -left-4 w-24 h-24 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-30"></div>
        <div className="absolute bottom-4 -right-4 w-24 h-24 bg-gradient-to-r from-teal-100 to-emerald-100 rounded-full mix-blend-multiply filter blur-xl opacity-30"></div>

        <div className="relative">
          <DialogHeader className="space-y-4 pb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <IconUser className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Nou Client
                </DialogTitle>
                <DialogDescription className="text-gray-600 mt-1">
                  Afegeix un nou client al sistema. Els camps marcats amb * són obligatoris.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[70vh] pr-2">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {/* Informació Personal */}
                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-6 shadow-lg">
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <IconUser className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                      Informació Personal
                    </h3>
                  </div>

                  <PersonForm
                    form={form}
                    fields={['nom', 'cognoms', 'data_naixement', 'genere', 'nacionalitat']}
                    required={['nom', 'cognoms']}
                  />

                  <Separator />

                  <FormField
                    control={form.control}
                    name="professio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Professió</FormLabel>
                        <FormControl>
                          <input {...field} placeholder="Enginyer, Professor, etc." className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-white/80 focus:border-emerald-500 focus:ring-emerald-500" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Document d'Identitat */}
                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-700">Document d'Identitat</h3>
                  <DocumentForm form={form} />
                </div>

                {/* Informació de Contacte */}
                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-6 shadow-lg">
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <IconMail className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                      Informació de Contacte
                    </h3>
                  </div>

                  <PersonForm
                    form={form}
                    fields={['email', 'telefon']}
                    required={['email']}
                  />
                </div>

                {/* Adreça */}
                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-6 shadow-lg">
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <IconMapPin className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                      Adreça
                    </h3>
                  </div>

                  <AddressForm form={form} />
                </div>

                {/* Estat */}
                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-6 shadow-lg">
                  <FormField
                    control={form.control}
                    name="estat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estat</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white/80 border-gray-200 focus:border-emerald-500 rounded-xl">
                              <SelectValue placeholder="Selecciona l'estat" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {estats.map((estat) => (
                              <SelectItem key={estat.value} value={estat.value}>
                                {estat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Notes */}
                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-700">Informació Addicional</h3>

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Notes addicionals sobre el client..."
                            className="min-h-[80px] bg-white/80 border-gray-200 focus:border-emerald-500 rounded-xl"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Botons */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={createClientMutation.isPending}
                    className="rounded-xl"
                  >
                    Cancel·lar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createClientMutation.isPending}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0 rounded-xl px-8 py-2.5"
                  >
                    {createClientMutation.isPending ? (
                      <>
                        <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creant...
                      </>
                    ) : (
                      'Crear Client'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
